import type { CSSProperties, ReactNode } from 'react';
import type { WindowRenderOptions } from '../Select/WindowRender';

export interface FilterValue {
    /**
     * The label
     */
    label: string;
    /**
     * The on remove callback
     */
    onRemove: () => void;
}

export type Variant<Type, Shape> = {
    /**
     * The type of filter
     */
    type: Type;
} & Shape;

export type FilterType =
    | 'search'
    | 'select'
    | 'multiSelect'
    | 'dateRange'
    | 'radio'
    | 'custom';

export interface CommonProps<T> {
    /**
     * The key
     */
    field: keyof Partial<T> & string;
    /**
     * The placeholder
     */
    placeholder?: string;
    /**
     * The filter type
     */
    type: FilterType;
    /**
     * The styles of filter
     */
    styles?: Pick<CSSProperties, 'minWidth' | 'maxWidth' | 'width' | 'flex'>;
}

export interface DateRangeValue {
    /**
     * The start date
     */
    startDate: string;
    /**
     * The end date
     */
    endDate: string;
}

export const isDateRangeValue = (value: unknown): value is DateRangeValue => {
    return (
        !!value &&
        !!(
            (value as DateRangeValue).startDate ||
            (value as DateRangeValue).endDate
        )
    );
};

export interface SelectProps<T> extends CommonProps<T> {
    /**
     * Items of select
     */
    items: {
        /**
         * The label
         */
        label: string;
        /**
         * The value
         */
        value: string;
    }[];

    /**
     * Show search box
     */
    searchable?: boolean;
    /**
     * Use the same width for popup as trigger
     */
    matchWidth?: boolean;
    /**
     * Use window render for items
     */
    windowRender?: WindowRenderOptions;
}

export interface DateRangeProps<T> extends CommonProps<T> {
    /**
     * Set start date
     */
    minDate?: Date;
}

export interface CustomProps<T> extends CommonProps<T> {
    /**
     *
     */
    component: ReactNode;
}

export type Filter<T = object> =
    | Variant<'search', CommonProps<T>>
    | Variant<'select', SelectProps<T>>
    | Variant<'multiSelect', SelectProps<T>>
    | Variant<'radio', SelectProps<T>>
    | Variant<'dateRange', DateRangeProps<T>>
    | Variant<'custom', CustomProps<T>>;

export interface CommonFiltrationProps<T> {
    /**
     * The filters model
     */
    filters: Filter<T>[];
    /**
     * The filters value
     */
    filterValue: T;
    /**
     * The empty value of filters
     */
    emptyValue: T;
    /**
     * The onChange callback
     */
    onChangeFilters: (filterValue: T) => void;
    /**
     * The total of items
     */
    total?: number;
}
